import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, MapPin, Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

interface LocationPickerProps {
    value: string;
    onChange: (next: string) => void;
    existing: string[];
}

export const LocationPicker = ({
    value,
    onChange,
    existing,
}: LocationPickerProps) => {
    const { t } = useTranslation();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [popoverView, setPopoverView] = useState<'list' | 'new'>('list');
    const [draft, setDraft] = useState('');

    useEffect(() => {
        if (!popoverOpen) {
            setPopoverView('list');
            setDraft('');
        }
    }, [popoverOpen]);

    const select = (next: string) => {
        onChange(next);
        setPopoverOpen(false);
    };

    return (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="flex h-10 w-full items-center justify-between whitespace-nowrap rounded-md border border-control bg-background px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                >
                    <span className="flex min-w-0 items-center gap-2">
                        <MapPin
                            className="h-4 w-4 shrink-0 text-subtle-foreground"
                            aria-hidden="true"
                        />
                        {value ? (
                            <span className="truncate text-foreground">
                                {value}
                            </span>
                        ) : (
                            <span className="text-subtle-foreground">
                                {t(
                                    'roomOverview.mriMachineForm.form.location.none'
                                )}
                            </span>
                        )}
                    </span>
                    <ChevronDown
                        className="h-4 w-4 shrink-0 opacity-50"
                        aria-hidden="true"
                    />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2">
                {popoverView === 'list' ? (
                    <div className="flex flex-col gap-1">
                        {existing.length > 0 && (
                            <ul className="flex flex-col gap-0.5">
                                {existing.map((location) => (
                                    <li key={location}>
                                        <button
                                            type="button"
                                            onClick={() => select(location)}
                                            className={cn(
                                                'flex w-full items-center rounded-md px-2 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-muted',
                                                value === location && 'bg-muted'
                                            )}
                                        >
                                            <span className="truncate">
                                                {location}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="my-1 h-px bg-subtle" />
                        <button
                            type="button"
                            onClick={() => select('')}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-subtle-foreground transition-colors hover:bg-muted"
                        >
                            <X className="h-3 w-3" aria-hidden="true" />
                            {t(
                                'roomOverview.mriMachineForm.form.location.none'
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setPopoverView('new')}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-muted"
                        >
                            <Plus className="h-3 w-3" aria-hidden="true" />
                            {t(
                                'roomOverview.mriMachineForm.form.location.newLocation'
                            )}
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2 p-1">
                        <Input
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (draft.trim()) select(draft.trim());
                                }
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setPopoverView('list');
                                }
                            }}
                            placeholder={t(
                                'roomOverview.mriMachineForm.form.location.namePlaceholder'
                            )}
                            autoFocus
                            className="h-8 text-xs"
                        />
                        <div className="flex justify-end gap-2 pt-1">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setPopoverView('list')}
                            >
                                {t(
                                    'roomOverview.mriMachineForm.form.location.cancel'
                                )}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                disabled={!draft.trim()}
                                onClick={() => select(draft.trim())}
                            >
                                {t(
                                    'roomOverview.mriMachineForm.form.location.save'
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
